// Alerts for a restaurant's staff (guests' requests, tickets ready to collect):
// what counts as new, the tab title, and two per-device choices kept in
// localStorage (the sound switch and the ready tickets this device dismissed).
import { useSyncExternalStore } from 'react'

/** How long a ready ticket stays in the bell. */
export const READY_WINDOW_MS = 30 * 60 * 1000

/**
 * The ids that arrived since the last look. The first look (`seen` null) is a
 * baseline: what is already waiting when the app opens alerts nobody.
 */
export function newIds(seen: ReadonlySet<string> | null, current: readonly string[]): string[] {
  if (!seen) return []
  return current.filter((id) => !seen.has(id))
}

/** "(2) Sano Godam" while something waits, the plain title otherwise. */
export function alertTitle(count: number, base: string): string {
  const plain = base.replace(/^\(\d+\)\s*/, '')
  return count > 0 ? `(${count}) ${plain}` : plain
}

/** A ready ticket still worth showing: marked ready inside the window. */
export function readyIsFresh(readyAt: string | null, nowMs: number): boolean {
  return readyAt !== null && Date.parse(readyAt) > nowMs - READY_WINDOW_MS
}

/**
 * Who hears an alert now (docs/plans/alert-servers.md): `mine` (this person serves that
 * table: at once), `all` (a free table, or waited `waitMs` for its server: everyone) or
 * `quiet` (someone else's table, still within its wait: in the bell, no pop-up).
 */
export type AlertLevel = 'mine' | 'all' | 'quiet'

export function alertLevel(a: {
  serverId: string | null
  me: string
  firstSeenMs: number
  nowMs: number
  waitMs: number
}): AlertLevel {
  if (a.serverId === null) return 'all'
  if (a.serverId === a.me) return 'mine'
  return a.nowMs - a.firstSeenMs >= a.waitMs ? 'all' : 'quiet'
}

// When this device first saw each alert. Phones' clocks drift, so the server's times are
// not used for the wait; a reload starts it again.
const firstSeen = new Map<string, number>()

/** When this device first saw the alert `id` (now, the first time it is asked). */
export function firstSeenAt(id: string, nowMs: number): number {
  const at = firstSeen.get(id)
  if (at !== undefined) return at
  firstSeen.set(id, nowMs)
  return nowMs
}

/** "1 min", "2 min": how long an escalated alert has waited, in whole minutes (at least 1). */
export function waitedLabel(ms: number): string {
  return `${Math.max(1, Math.floor(ms / 60_000))} min`
}

const SOUND_KEY = 'ims.alerts.sound'
const DISMISSED_KEY = 'ims.alerts.dismissed'
const DISMISSED_MAX = 200
const listeners = new Set<() => void>()

function readSound(): boolean {
  try {
    return localStorage.getItem(SOUND_KEY) !== 'off'
  } catch {
    return true
  }
}
function readDismissed(): string[] {
  try {
    const value: unknown = JSON.parse(localStorage.getItem(DISMISSED_KEY) ?? '[]')
    return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : []
  } catch {
    return []
  }
}

let sound = readSound()
let dismissed: readonly string[] = readDismissed()

function changed() {
  for (const listener of listeners) listener()
}
function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export const soundOn = () => sound

export function setSound(on: boolean) {
  sound = on
  try {
    if (on) localStorage.removeItem(SOUND_KEY)
    else localStorage.setItem(SOUND_KEY, 'off')
  } catch {
    // Storage blocked: the choice still holds until the app is closed.
  }
  changed()
}

/** Hides a ready ticket on this device (other devices still show it). */
export function dismissReady(ticketId: string) {
  if (dismissed.includes(ticketId)) return
  dismissed = [...dismissed, ticketId].slice(-DISMISSED_MAX)
  try {
    localStorage.setItem(DISMISSED_KEY, JSON.stringify(dismissed))
  } catch {
    // Storage blocked: it stays hidden until the app is closed.
  }
  changed()
}

export function useAlertSound(): boolean {
  return useSyncExternalStore(subscribe, () => sound)
}
export function useDismissedReady(): readonly string[] {
  return useSyncExternalStore(subscribe, () => dismissed)
}
